import { DIMENSIONS, type Report } from './model';

const W = 1080;
const H = 1440;
const PAPER = '#F6EBCF';
const BLUE = '#1746A2';
const GOLD = '#F3B61F';
const CORAL = '#EF6A5B';
const INK = '#172136';
const SERIF = '"Songti SC", "Noto Serif SC", serif';

function wrap(ctx: CanvasRenderingContext2D, text: string, maxWidth: number) {
  const lines: string[] = [];
  for (const paragraph of text.split('\n')) {
    let line = '';
    for (const char of paragraph) {
      if (line && ctx.measureText(line + char).width > maxWidth) { lines.push(line); line = char; } else line += char;
    }
    lines.push(line);
  }
  return lines;
}

function text(ctx: CanvasRenderingContext2D, value: string, x: number, y: number, max: number, lineHeight: number, maxLines = 8) {
  wrap(ctx, value, max).slice(0, maxLines).forEach((line, i) => ctx.fillText(line, x, y + i * lineHeight));
}

function base(ctx: CanvasRenderingContext2D, page: number, title: string) {
  ctx.fillStyle = PAPER; ctx.fillRect(0, 0, W, H);
  ctx.strokeStyle = BLUE; ctx.lineWidth = 8; ctx.strokeRect(44, 44, W - 88, H - 88);
  ctx.strokeStyle = 'rgba(23,70,162,.25)'; ctx.lineWidth = 2; ctx.strokeRect(64, 64, W - 128, H - 128);
  for (let i = 0; i < 5; i++) {
    ctx.beginPath(); ctx.strokeStyle = `rgba(23,70,162,${.08 + i * .02})`;
    ctx.ellipse(830, 260, 90 + i * 34, 48 + i * 22, -.35, 0, Math.PI * 2); ctx.stroke();
  }
  ctx.fillStyle = BLUE; ctx.font = `700 30px ${SERIF}`; ctx.textAlign = 'left'; ctx.fillText('人生天赋罗盘 · 日光勘探记录', 90, 118);
  ctx.fillStyle = INK; ctx.font = `900 72px ${SERIF}`; text(ctx, title, 90, 260, 850, 90, 3);
  ctx.fillStyle = BLUE; ctx.font = `500 28px ${SERIF}`; ctx.textAlign = 'center';
  ctx.fillText(`小红书搜索：人生天赋罗盘   ·   ${page}/5`, W / 2, H - 82);
}

function compass(ctx: CanvasRenderingContext2D, report: Report, cx: number, cy: number, radius: number) {
  const dims = Object.keys(DIMENSIONS) as (keyof typeof DIMENSIONS)[];
  [0.25, .5, .75, 1].forEach((scale) => { ctx.beginPath(); ctx.strokeStyle = 'rgba(23,70,162,.18)'; ctx.arc(cx, cy, radius * scale, 0, Math.PI * 2); ctx.stroke(); });
  const points = dims.map((d, i) => {
    const angle = -Math.PI / 2 + i / dims.length * Math.PI * 2;
    return [cx + Math.cos(angle) * radius * report.scores[d] / 100, cy + Math.sin(angle) * radius * report.scores[d] / 100] as const;
  });
  ctx.beginPath(); points.forEach(([x, y], i) => i ? ctx.lineTo(x, y) : ctx.moveTo(x, y)); ctx.closePath();
  ctx.fillStyle = 'rgba(243,182,31,.38)'; ctx.fill(); ctx.strokeStyle = BLUE; ctx.lineWidth = 6; ctx.stroke();
  dims.forEach((d, i) => { const angle = -Math.PI / 2 + i / dims.length * Math.PI * 2; ctx.fillStyle = INK; ctx.font = `600 27px ${SERIF}`; ctx.textAlign = 'center'; ctx.fillText(DIMENSIONS[d].name, cx + Math.cos(angle) * (radius + 58), cy + Math.sin(angle) * (radius + 58)); });
}

export async function renderShareCards(report: Report): Promise<Blob[]> {
  const draws: ((ctx: CanvasRenderingContext2D) => void)[] = [
    (ctx) => { base(ctx, 1, report.title); ctx.fillStyle = BLUE; ctx.font = `800 34px ${SERIF}`; ctx.fillText(`${report.mode}天赋组合`, 90, 520); ctx.fillStyle = CORAL; ctx.font = `900 82px ${SERIF}`; ctx.fillText(`${DIMENSIONS[report.top[0]].name} × ${DIMENSIONS[report.top[1]].name}`, 90, 640); ctx.fillStyle = INK; ctx.font = `500 38px ${SERIF}`; text(ctx, report.summary, 90, 760, 820, 62, 6); },
    (ctx) => { base(ctx, 2, '我的十维天赋罗盘'); compass(ctx, report, W / 2, 790, 315); ctx.fillStyle = CORAL; ctx.font = `800 34px ${SERIF}`; ctx.textAlign = 'center'; ctx.fillText(`主峰 ${DIMENSIONS[report.top[0]].name} · 隐藏高地 ${DIMENSIONS[report.top[2]].name}`, W / 2, 1190); },
    (ctx) => { base(ctx, 3, '我更容易在哪些场景发光'); const entries = Object.entries(report.sceneScores).sort((a, b) => b[1] - a[1]); entries.forEach(([scene, score], i) => { const y = 470 + i * 150; ctx.fillStyle = INK; ctx.font = `700 36px ${SERIF}`; ctx.textAlign = 'left'; ctx.fillText(scene, 100, y); ctx.fillStyle = 'rgba(23,70,162,.12)'; ctx.fillRect(260, y - 38, 650, 46); ctx.fillStyle = i < 2 ? GOLD : BLUE; ctx.fillRect(260, y - 38, 650 * score / 100, 46); ctx.fillStyle = INK; ctx.fillText(String(score), 930, y); }); },
    (ctx) => { base(ctx, 4, '优势很强，也会用力过猛'); ctx.fillStyle = CORAL; ctx.font = `900 46px ${SERIF}`; ctx.fillText('我的过载警报', 90, 490); ctx.fillStyle = INK; ctx.font = `500 38px ${SERIF}`; report.overuse.slice(0, 3).forEach((item, i) => text(ctx, `${i + 1}. ${item}`, 90, 610 + i * 180, 850, 58, 3)); },
    (ctx) => { base(ctx, 5, '接下来7天 我先验证一次'); ctx.fillStyle = BLUE; ctx.font = `800 42px ${SERIF}`; ctx.fillText('DAY 1', 90, 500); ctx.fillStyle = INK; ctx.font = `700 52px ${SERIF}`; text(ctx, report.experiments[0].action, 90, 620, 850, 72, 5); ctx.fillStyle = CORAL; ctx.font = `700 34px ${SERIF}`; ctx.fillText('验证问题', 90, 980); ctx.fillStyle = INK; ctx.font = `500 38px ${SERIF}`; text(ctx, report.experiments[0].check, 90, 1060, 850, 58, 4); },
  ];
  const blobs: Blob[] = [];
  for (const draw of draws) {
    const canvas = document.createElement('canvas'); canvas.width = W; canvas.height = H;
    const ctx = canvas.getContext('2d')!; draw(ctx);
    const blob = await new Promise<Blob>((resolve, reject) => canvas.toBlob((b) => b ? resolve(b) : reject(new Error('生成图片失败')), 'image/png'));
    blobs.push(blob);
  }
  return blobs;
}

export async function shareToXhs(report: Report) {
  const blobs = await renderShareCards(report);
  const files = blobs.map((blob, i) => new File([blob], `人生天赋罗盘-${i + 1}.png`, { type: 'image/png' }));
  const data: ShareData = { title: `我的天赋组合是${report.title}`, text: report.shareCopy, files };
  try {
    if (navigator.share && (!navigator.canShare || navigator.canShare(data))) { await navigator.share(data); return '已打开系统分享面板'; }
  } catch (error) { if ((error as Error).name === 'AbortError') return '已取消分享'; }
  await navigator.clipboard.writeText(report.shareCopy).catch(() => undefined);
  blobs.forEach((blob, i) => setTimeout(() => { const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `人生天赋罗盘-${i + 1}.png`; a.click(); setTimeout(() => URL.revokeObjectURL(a.href), 1000); }, i * 320));
  return '已保存全部图片，并复制笔记文案';
}
